import React, { useRef } from "react";
import { useMarketoForm } from "@qlik/gatsby-plugin-marketo/hooks";
import {
    addPurserValuesToMarketoForms,
    trustAssessor,
    stitchSignup,
    basicActionAfterSubmission,
    formAzureSubmit,
    createTrial,
} from "../utils/form-functions";
import { formValidationMessages } from "../translations/formValidationMessages";
import { renderRichText } from "gatsby-source-contentful/rich-text";
import formatContentfulRichText from "@qlik/qlik/src/lib/format-rich-text-content";
import camelCase from "lodash/camelCase";
import { useI18n } from "@qlik/utils/i18nContext";
import { commonTranslations } from "@qlik/qlik/src/translations/common";

// ENV vars are a string of comma-separated key:value pairs, e.g. "formId1:value1,formId2:value2"
let replacementPairs = process.env.GATSBY_SANDBOX_FORM_ID_PAIRS?.split(",") ?? [];

// Turn formIDPairs into an object for easier lookup
replacementPairs = replacementPairs.reduce((acc, pair) => {
    const [key, value] = pair.split(":");
    acc[key] = value;
    return acc;
}, {});

const MarketoForm = ({
    buttonLabel,
    className,
    conversionType,
    form = null,
    formCallback: formCallbackType,
    formId,
    heading,
    isDark,
    onFormSuccess,
    programName,
    successUrl,
    thankYouMessage,
    twoStepForm,
}) => {
    // Grab the i18n helper.
    const { i18n } = useI18n();

    // Define a ref for the form element
    const formRef = useRef(null);
    const submitButtonRef = useRef(null);

    // If a form element is passed, assign the heading and formID from that passed object, re: "Gated_Content/index.jsx"
    heading = form?.heading?.raw ?? heading;
    formId = String(form?.formId ?? formId);

    // If the formID is in the replacementPairs object, and we're not on Production, replace it with the sandbox form ID
    if (replacementPairs[formId]) {
        console.info(`> Replacing formId ${formId} with sandbox form ID ${replacementPairs[formId]}.`);
        formId = replacementPairs[formId];
    }

    // Get two-letter language code from locale
    const langCode = i18n.curr.langCode;

    // Get the validation messages for the current language
    const validationMessages = formValidationMessages[langCode];

    // Function which toggles a class on the parent element of the input field, provides a visual queue when focused
    const marketoLabelHelper = () => {
        // Get all inputs which are not checkboxes or radio buttons
        const inputs = formRef?.current?.querySelectorAll(".mktoField:not([type='checkbox']):not([type='radio'])");

        // For each of the inputs available
        inputs?.forEach((input) => {
            // Add the 'active' class to the parent element of the input field when focusing in
            input.addEventListener("focus", () => {
                input.parentNode.classList.add("active");
            });

            // Remove the 'active' class from the parent element of the input field when focusing out
            input.addEventListener("blur", () => {
                // If the input field has a value, keep the 'active' class on the parent element ("force" argument)
                input.parentNode.classList.toggle("active", Boolean(input.value));
            });
        });
    };

    // Function which add a class to the parent of the matching selector
    const addClassToParent = (selector, classNameStr) => {
        // Get all elements of the passed selector
        const elements = formRef?.current?.querySelectorAll(selector);

        // For each of the matching elements
        elements?.forEach((element) => {
            // Get the target parent node, 3 layers up
            const target = element.parentNode?.parentNode?.parentNode;

            // If a parent is present, add the className to it
            if (target) target.classList.add(classNameStr);
        });
    };

    // Function which adds classes to radio lists and logical fields
    const marketoAddClass = () => {
        addClassToParent(".mktoRadioList", "radio-list-wrapper");
        addClassToParent(".mktoLogicalField", "logical-field-wrapper");
    };

    // Function which clears the first option for all select elements
    const selectEmptyOption = () => {
        // Get all select elements within the form element
        const selects = formRef?.current?.querySelectorAll("select");

        // For each of the select elements, clear the first option
        selects?.forEach((select) => {
            select.querySelector("option:first-child").innerHTML = "";
        });
    };

    // Function which resizes textarea on input
    const textareaResize = () => {
        // Get all textarea elements within the form element
        const allTextareas = formRef?.current?.querySelectorAll(".mktoFieldWrap textarea");

        // For each of the textarea elements
        allTextareas?.forEach((textarea) => {
            // Add an event listener which will increase the height of the element as the user types
            textarea.addEventListener("input", () => {
                textarea.style.height = "auto";
                textarea.style.height = `${textarea.scrollHeight}px`;
            });
        });
    };

    // Function which sets the submit button label to the value from CMS
    const setSubmitButtonLabel = (submitBtn) => {
        // optional chaining assignment is experimental
        if (submitBtn && buttonLabel) submitBtn.innerText = buttonLabel;
    };

    const clearInvalidFields = (el) => {
        const invalidFields = el.querySelectorAll(".mktoInvalid");
        invalidFields.forEach((field) => field.classList.remove("mktoInvalid"));
    };

    // Function which splits the visible Marketo forms into two steps
    // 1. Splits the visible form fields into two groups
    // 2. Creates a div to hold the first step fields
    // 3. Adds a "next" button to the first step fields
    // 4. Adds a "previous" button to the remaining fields
    const twoStepMarketoFormInit = (form) => {
        // Get the form element
        const formElement = formRef?.current;

        // Get the visible form fields (non-hidden)
        let visibleFields = Array.from(formElement.querySelectorAll("div.mktoFieldDescriptor"));

        // Get parent elements of the visible fields
        visibleFields = visibleFields.map((field) => field.parentNode);

        // Split the visibleFieldParents into two groups
        const firstStepFields = visibleFields.slice(0, Math.floor(visibleFields.length / 2));

        // Create a div to hold the first step fields
        const firstStepFieldsDiv = document.createElement("div");
        firstStepFieldsDiv.classList.add("firstStep");

        // Add the first step fields to the first-step-fields div
        for (const field of firstStepFields) {
            firstStepFieldsDiv.appendChild(field);
        }

        // Hide the submit button
        const submitButtonRow = formElement.querySelector(".mktoButtonRow");
        submitButtonRow.style.display = "none";

        // Add a "next" button to the first step fields
        const nextButton = document.createElement("button");

        // Abstracted function to show the second step fields ("Next" action)
        const showSecondStepFields = () => {
            // use rAF() to ensure focus and class changes happen after the click event
            requestAnimationFrame(() => {
                // Get all inputs, selects, and textareas in the first step
                let firstStepInputs = firstStepFieldsDiv.querySelectorAll("input, select, textarea");

                // Reverse the order of the inputs, so the first input is focused first
                firstStepInputs = Array.from(firstStepInputs).reverse();

                // Focus empty inputs so Marketo will designate as invalid or required
                for (const input of firstStepInputs) {
                    if (!input.value) input.focus();
                }

                // If none of the first-step fields have the className "mktoInvalid", and none of the inputs are empty
                if (
                    !firstStepFieldsDiv.querySelector(".mktoInvalid") &&
                    !firstStepFieldsDiv.querySelector(".mktoError")
                ) {
                    // Hide the first step fields entirely
                    firstStepFieldsDiv.style.display = "none";

                    // For each "second step" (non-first-step) field, show them
                    for (const el of formElement.querySelectorAll("& > .mktoFormRow")) {
                        el.style.display = null;
                    }

                    // Show the submit button and the back button
                    submitButtonRow.style.display = null;
                    backButton.style.display = null;

                    requestAnimationFrame(() => {
                        // Remove .mktoInvalid class from all input elements in the second step
                        clearInvalidFields(formElement);

                        // Focus the first input in the second step
                        formElement.querySelector("& > .mktoFormRow .mktoField:not([type='hidden'])").focus();
                    });
                }
            });
        };

        // TODO: Revisit after translation work -- nextButton.innerText = twoStepFormNextButtonLabel ?? commonTranslations[langCode].next;
        nextButton.innerText = commonTranslations[langCode].next;
        nextButton.classList.add("nextButton");
        nextButton.addEventListener("click", (e) => {
            // Prevent default action
            e.preventDefault();

            // Kick off a validation of the form now, so we can review the validity of the inputs (if any)
            form.validate();

            // Continue to the second step
            showSecondStepFields();
        });

        // Wrap the next button in a span with the class name mktoButtonWrap mktoMinimal
        const nextButtonWrap = document.createElement("span");
        nextButtonWrap.classList.add("mktoButtonWrap", "mktoMinimal");
        nextButtonWrap.appendChild(nextButton);

        // Wrap the span in a div with the class name mktoButtonRow
        const nextButtonRow = document.createElement("div");
        nextButtonRow.classList.add("mktoButtonRow");
        nextButtonRow.appendChild(nextButtonWrap);

        // Add the next button to the first-step-fields div
        firstStepFieldsDiv.appendChild(nextButtonRow);

        // Add a "back" button to the remaining fields
        const backButton = document.createElement("button");

        // Abstracted function to show the first step fields ("Previous" action)
        const showFirstStepFields = () => {
            // Hide the submit button, the back button, and remaining fields outside of the first step
            backButton.style.display = "none";
            submitButtonRow.style.display = "none";
            for (const el of formElement.querySelectorAll("& > .mktoFormRow")) {
                el.style.display = "none";
            }

            // Show the first step fields
            firstStepFieldsDiv.style.display = null;
        };

        // Expose firstStepFieldsDiv el and showFirstStepFields() func for usage in the i18nGlobalValidationEmailError function
        twoStepMarketoFormInit.firstStepFieldsDiv = firstStepFieldsDiv;
        twoStepMarketoFormInit.showFirstStepFields = showFirstStepFields;

        // TODO: Support i18n "previous" string translation
        // TODO: Revisit after translation work -- backButton.innerText = `\u2039 ${twoStepFormBackButtonLabel ??
        backButton.innerText = `\u2039 ${commonTranslations[langCode].previous}`;
        backButton.classList.add("prevButton");
        backButton.addEventListener("click", (e) => {
            // Prevent default action
            e.preventDefault();

            // Show the first step fields
            showFirstStepFields();
        });

        // Add the first-step div to the form as the first children
        formElement.insertBefore(firstStepFieldsDiv, formElement.firstChild);

        // Wrap the next button in a span with the class name mktoButtonWrap mktoMinimal
        const backButtonWrap = document.createElement("span");
        backButtonWrap.classList.add("textLinkWrap", "mktoMinimal");
        backButtonWrap.appendChild(backButton);

        // Place the back button as the first element in the submitButtonRow
        submitButtonRow.appendChild(backButtonWrap);

        // Adjust the visual order of buttons
        backButton.classList.add("textLink");

        // Hide the "second step" (non-first-step) fields
        const mktoFormRows = formElement.querySelectorAll("& > .mktoFormRow");
        for (const el of mktoFormRows) {
            el.style.display = "none";
        }
    };

    // Handle "Global Form Validation" errors, which happen "on the wire" *AFTER* the form is submitted
    const i18nGlobalValidationEmailError = (formElement) => {
        // MO to watch for changes in the form, and show a corresponding div if an error is present
        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                // If the mutation is an addition of a .mktoError class
                if (mutation?.addedNodes.length > 0 && mutation.addedNodes[0]?.classList?.contains("mktoError")) {
                    // The errrorMessageParent which contains two div elements
                    const errorMessageParent = mutation.addedNodes[0];

                    // Get the div with the id of "ValidMsgEmail" and update the text
                    const emailError = errorMessageParent.querySelector("div[id='ValidMsgEmail']");
                    if (emailError) emailError.innerText = validationMessages.personal_email_error;

                    // If the error is part of the first step fields, show the first step fields ("previous" action)
                    if (
                        twoStepForm?.[0] === "Yes" &&
                        twoStepMarketoFormInit.firstStepFieldsDiv.contains(mutation.addedNodes[0])
                    )
                        twoStepMarketoFormInit.showFirstStepFields();

                    // Add a loading spinner to the submit button via CSS
                    formRef.current?.classList?.remove("has-spinner");
                }
            }
        });

        // Kick off the observer
        observer.observe(formElement, {
            attributes: true,
            childList: true,
            subtree: true,
        });
    };

    // Helper function which removes styles from Marketo forms
    const destyleMktoForm = (formEl, optionsParam) => {
        const arrayFrom = Function.prototype.call.bind(Array.prototype.slice);
        const options = optionsParam || {};

        // remove element styles from <form> and children
        if (!options.keepInline && formEl?.querySelectorAll("[style]")) {
            const styledEls = arrayFrom(formEl.querySelectorAll("[style]")).concat(formEl);
            for (const el of styledEls) {
                el.removeAttribute("style");
            }

            // Remove all <style> element contents from the form
            const styleEls = formEl.querySelectorAll("style");
            for (const el of styleEls) {
                el.parentNode.removeChild(el);
            }
        }

        // Disable remote stylesheets and local styles
        if (!options.keepSheets && formEl) {
            const styleSheets = arrayFrom(document.styleSheets);
            for (const ss of styleSheets) {
                if (
                    (ss?.ownerNode.id === "mktoForms2BaseStyle" || ss?.ownerNode.id === "mktoForms2ThemeStyle") &&
                    formEl.contains(ss.ownerNode)
                ) {
                    ss.disabled = true;
                }
            }
        }

        // Mark the form as ready for styles
        if (!options.moreStyles && formEl) formEl.setAttribute("data-styles-ready", "true");
    };

    // Helpers to be called when the form is rendering
    const marketoFormRenderHelper = () => {
        // Add 'active' class to input fields on focus and focusout
        marketoLabelHelper();

        // Add specific classes to radio lists and logical fields
        marketoAddClass();

        // Clear the first option for all select elements
        selectEmptyOption();

        // Resize textarea on input
        textareaResize();

        submitButtonRef.current = formRef?.current?.querySelector(".mktoButton[type='submit']");

        // Set submit button label to the value from CMS
        setSubmitButtonLabel(submitButtonRef.current);
    };

    // Called when *loading* a form
    const formLoadCallback = (form) => {
        // Remove any Marketo styles from the form
        destyleMktoForm(formRef.current);

        // Ensure the form is visible
        if (formRef.current) formRef.current.style.opacity = 1;

        // If chosen by Production, split the visible Marketo forms into two steps
        if (twoStepForm && twoStepForm[0] === "Yes") twoStepMarketoFormInit(form);

        // Define thank you message
        thankYouMessage = thankYouMessage ?? validationMessages.thank_you_text;

        // Map the form conversion type to the Marketo conversion type
        const conversionMap = {
            undefined: "none",
            "None Set": "none",
            "Demo Request": "demo",
            "Free Trial Signup": "trial",
            "Content Download": "content",
            "Webinar Signup": "webinar",
            "Contact Sales": "contact",
            TOS: "tos",
        };

        // Set the Marketo conversion type based on the mapping, gets passed down the line to Marketo
        conversionType = conversionMap[conversionType];

        // Ensure MktoForms2 is available for use
        if (typeof window !== "undefined" && window.MktoForms2) {
            // Attaching overall form error message to form object
            form.form_submission_error = validationMessages.form_submission_error;

            // Add values from the Purser to the forms
            addPurserValuesToMarketoForms(form);

            // For "Gated Resources", an "onFormSuccess" callback is passed in from the parent component
            // See the AccessForm.jsx or the Gated_Content component for more information
            if (onFormSuccess) {
                form.onSubmit(() => {
                    // Handle "Global Form Validation" errors, which happen "on the wire" *AFTER* the form is submitted
                    i18nGlobalValidationEmailError(formRef.current);
                });

                // Set the success handler
                form.onSuccess(() => {
                    // Fire the passed callback function
                    onFormSuccess(formId);

                    // Prevent the form from submitting
                    return false;
                });
            } else {
                // Otherwise, if the form isn't being used as a "Gated Resource":
                // Define a queue name for Azure functions
                const queueName = ["Azure - Qlik Sense Business", "Azure - Talend Trial"].includes(formCallbackType)
                    ? "prs-queue"
                    : "marketo-queue";

                // Conditionally set programName as a hidden value
                if (programName) form.addHiddenFields({ programName });

                // Conditionally submit though different submission mechanisms
                switch (formCallbackType) {
                    // Case for both Replicate Trial and Talend Trial
                    case "Talend Trial":
                    case "Replicate Trial":
                        // Submit's to the one of the Trial APIs and then Marketo – both via Netlify functions
                        form.onSubmit(() => {
                            // Prevent form default submission behavior
                            form.submittable(false);

                            // Add a loading spinner to the submit button via CSS
                            formRef.current?.classList?.add("has-spinner");

                            // Get the form values
                            const formValues = form.getValues();

                            // Define the callback action for creating the trial
                            const trialCallbackAction = () =>
                                basicActionAfterSubmission(form, i18n, thankYouMessage, conversionType, successUrl);

                            // Adjust the formCallbackType value to camelCase the form callback choice
                            // This trialName should be equal to the Netlify function we want to call
                            const trialName = camelCase(formCallbackType);

                            // Execute the createTrial function which submits to the corresponding Netlify function
                            createTrial(trialName, formValues, form, true, trialCallbackAction);
                        });
                        break;
                    case "Azure - Talend Trial":
                        form.onSubmit(async () => {
                            // Prevent form default submission behavior, continue below:
                            form.submittable(false);

                            // Find the submit button, disable it, and change it's text to "submitting..."
                            const submitButton = formRef.current.querySelector(".mktoButton[type='submit']");
                            submitButton.disabled = true;

                            // Get the form values
                            const formValues = form.getValues();

                            // Adjust the formCallbackType value to camelCase the form callback choice
                            // This trialName should be equal to the Netlify function we want to call
                            const trialName = camelCase(formCallbackType);

                            // Execute the createTrial function which submits to the corresponding Netlify function
                            // azureTalendTrial() is a placeholder (returns 200) -- createTrial() submits to Marketo
                            await createTrial(trialName, formValues, form, true, () => true);

                            // Sends to Azure Netlify function which submits to one of the Azure functions
                            await formAzureSubmit(
                                form,
                                i18n,
                                thankYouMessage,
                                conversionType,
                                queueName,
                                successUrl,
                                programName,
                                formCallbackType
                            );
                        });
                        break;
                    case "Azure - Marketo":
                    case "Azure - Qlik Sense Business":
                        form.onSubmit(() => {
                            // Prevent form default submission behavior, continue below:
                            form.submittable(false);

                            // Sends to Azure Netlify function which submits to one of the Azure functions
                            formAzureSubmit(
                                form,
                                i18n,
                                thankYouMessage,
                                conversionType,
                                queueName,
                                successUrl,
                                programName,
                                formCallbackType
                            );
                        });
                        break;
                    case "Trust Assessor":
                        // Will create an account in the Talend Trust Assessor App, and redirect there
                        form.onSubmit(() => {
                            // Prevent form default submission behavior
                            form.submittable(false);

                            // Add a loading spinner to the submit button via CSS
                            formRef.current?.classList?.add("has-spinner");

                            // Execute the Trust Assessor function
                            trustAssessor(form);
                        });
                        break;
                    case "Stitch Signup":
                        // Will redirect the user a Stitch Sign Up form with the email pre-filled.
                        form.onSubmit(() => {
                            // Prevent form default submission behavior
                            form.submittable(false);

                            // Add a loading spinner to the submit button via CSS
                            formRef.current?.classList?.add("has-spinner");

                            // Execute the Stitch Sign Up function
                            stitchSignup(form, successUrl);
                        });
                        break;
                    default:
                        form.onSubmit(() => {
                            // Add a loading spinner to the submit button via CSS
                            formRef.current?.classList?.add("has-spinner");

                            // Handle "Global Form Validation" errors, which happen "on the wire" *AFTER* the form is submitted
                            i18nGlobalValidationEmailError(formRef.current);
                        });

                        form.onSuccess(() => {
                            return basicActionAfterSubmission(form, i18n, thankYouMessage, conversionType, successUrl);
                        });
                        break;
                }
            }
        }
    };

    // Hook which either initiates MktoForms2 and/or loads the form (if MktoForms2 is present already)
    useMarketoForm({
        formId, // The Marketo form ID
        // The callback function to be called when the form is *loaded*
        callback: formLoadCallback,
        renderFunction: marketoFormRenderHelper, // Function to be called when the form is *rendered*,
    });

    if (heading) {
        heading = heading?.raw ? (
            <div className="formHeader">{renderRichText(heading, formatContentfulRichText())}</div>
        ) : (
            <div className="formHeader">{heading}</div>
        );
    }

    return (
        <div className={className}>
            <div className={`formHolder ${isDark ? "formDark" : ""} form-id-${formId}`}>
                {heading && (
                    <h2 className={`title-3 mb-3.75 pb-5 lg:pb-11 ${isDark ? "text-white" : "text-blue"}`}>
                        {heading}
                    </h2>
                )}
                <form
                    id={`mktoForm_${formId}`}
                    ref={formRef}
                    className="!w-full"
                    name={formCallbackType && formCallbackType}
                    style={{ opacity: 0 }}
                >
                    <span className="hidden mktoButton mktoButtonInitEl">{buttonLabel}</span>
                </form>
            </div>
        </div>
    );
};

export default MarketoForm;
